import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export const POST_META = {
  slug: 'crystal-ball',
  title:
    'The Warehouse Crystal Ball: Using AI to Predict Demand Before It Happens',
  description:
    'Tired of running out of bestsellers while sitting on slow-moving stock? Learn how AI-powered demand forecasting calculates sales velocity per SKU, surfaces confidence-scored predictions, and turns forecasting intelligence into one-click purchase orders.',
  date: 'May 9, 2026',
  dateIso: '2026-05-09',
  readTime: '9 min read',
  category: 'AI & Forecasting',
  heroImage: '/blog-forecast-hero.jpg',
  ogImage: '/blog-forecast-hero.jpg',
};

export default function CrystalBallPost() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [heatFailed, setHeatFailed] = createSignal(false);
  const [dashFailed, setDashFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: POST_META.title,
      description: POST_META.description,
      datePublished: POST_META.dateIso,
      dateModified: POST_META.dateIso,
      image: `https://khoai.io${POST_META.heroImage}`,
      url: 'https://khoai.io/blogs/crystal-ball',
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-crystal-ball'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-crystal-ball';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-crystal-ball-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-crystal-ball-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-crystal-ball')?.remove();
    document.getElementById('ld-json-blog-crystal-ball-bc')?.remove();
  });

  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const h3 = 'mt-8 text-xl font-bold text-gray-900';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">AI &amp; Forecasting</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            {POST_META.category}
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {POST_META.title}
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            {POST_META.description}
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>{POST_META.date}</span>
            <span>·</span>
            <span>{POST_META.readTime}</span>
          </div>
        </header>

        {/* Hero image — 1200×628 */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Mobile kiosk in dim warehouse showing a predictive demand chart
              </div>
            }
          >
            <img
              src="/blog-forecast-hero.jpg"
              alt="A backlit mobile kiosk screen in a dim warehouse displaying a colorful predictive chart with an upward demand spike labeled 'Next Week: +40% Confidence'"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Body ── */}
        <div class="mt-12">
          {/* Hook: Ghost Shelf */}
          <p class={p}>
            It&apos;s Wednesday afternoon. You&apos;re standing in front of
            Shelf B-07 — home to your single best-selling SKU — staring at empty
            air. The shelf has been bare since Sunday. Three days of orders have
            come in; three days of customers have been told &ldquo;out of
            stock.&rdquo; The replacement shipment won&apos;t arrive until
            Friday at the earliest.
          </p>
          <p class={p}>
            Two aisles over sits a pallet of a product you overstocked four
            months ago, which hasn&apos;t moved once since. You paid to store
            it. You paid freight to receive it. And you&apos;re still paying
            both costs every day it doesn&apos;t sell.
          </p>
          <p class={p}>
            This is the{' '}
            <strong class="font-semibold text-gray-900">
              double-edged sword of reactive inventory
            </strong>
            : you run out of what sells and drown in what doesn&apos;t. It
            isn&apos;t a purchasing mistake. It&apos;s a <em>visibility</em>{' '}
            mistake — and it&apos;s the exact problem AI forecasting is designed
            to eliminate.
          </p>

          {/* Section 1: The Mismatch Penalty */}
          <h2 class={h2}>
            The Mismatch Penalty: Calculating What &ldquo;Getting It
            Wrong&rdquo; Actually Costs
          </h2>
          <p class={p}>
            Most warehouse operators think of stockouts and overstock as
            occasional inconveniences. They&apos;re not. They&apos;re a
            persistent, quantifiable tax on your margins — one you can measure.
          </p>

          <div class="mt-8 grid gap-4 sm:grid-cols-2">
            <div class="rounded-2xl border border-red-200 bg-red-50 p-5">
              <p class="text-sm font-bold uppercase tracking-wide text-red-500">
                Cost of a Stockout
              </p>
              <ul class="mt-3 space-y-1.5 text-sm leading-6 text-gray-700">
                <li>✗ Lost sale revenue (immediate)</li>
                <li>✗ Customer switches to a competitor</li>
                <li>✗ Expedited shipping to recover</li>
                <li>✗ Negative review or refund request</li>
                <li>✗ Staff time managing apologies</li>
              </ul>
            </div>
            <div class="rounded-2xl border border-blue-200 bg-blue-50 p-5">
              <p class="text-sm font-bold uppercase tracking-wide text-blue-500">
                Cost of Overstock
              </p>
              <ul class="mt-3 space-y-1.5 text-sm leading-6 text-gray-700">
                <li>✗ Capital tied up in unsold goods</li>
                <li>✗ Storage space at premium cost per m²</li>
                <li>✗ Risk of spoilage or obsolescence</li>
                <li>✗ Insurance on excess inventory</li>
                <li>✗ Markdown pressure to clear stock</li>
              </ul>
            </div>
          </div>

          <p class={p}>
            The harsh math: a single bestseller stockout of three days across 20
            orders per day at an average order value of $20 is $1,200 in lost
            revenue in one week. Not a bad month. One shelf. One week.
          </p>
          <p class={p}>
            The goal of AI forecasting isn&apos;t to be a trendy feature —
            it&apos;s to collapse that number toward zero.
          </p>

          {/* Section 2: Why Averages Fail */}
          <h2 class={h2}>Why Last Month&apos;s Average Is the Wrong Number</h2>
          <p class={p}>
            Traditional reorder logic works like this: you calculate average
            monthly sales, set a reorder point at &ldquo;2 weeks of
            stock,&rdquo; and check it manually at the end of each month. This
            was reasonable in 2005. It&apos;s a liability in 2026.
          </p>
          <p class={p}>
            Here&apos;s why. Averages smooth everything out — including the
            signals that matter most:
          </p>
          <ul class={ul}>
            <li>
              A product that sold 10 units in January, 10 in February, and 80 in
              March has an &ldquo;average&rdquo; of 33 — a number that perfectly
              describes no month.
            </li>
            <li>
              A regional holiday that doubles foot traffic in your area for two
              weeks doesn&apos;t appear in last year&apos;s data if you were
              closed or smaller.
            </li>
            <li>
              A competitor going out of business last quarter sends a wave of
              new customers your way — a one-time event that simple trend models
              will assume is permanent.
            </li>
          </ul>
          <p class={p}>
            AI-powered forecasting doesn&apos;t use a single average. It
            continuously recalculates{' '}
            <strong class="font-semibold text-gray-900">
              sales velocity per SKU
            </strong>{' '}
            — how fast each product is actually moving <em>right now</em>,
            weighted against seasonality patterns, recent trend shifts, and
            supplier lead times — all simultaneously.
          </p>

          {/* Heat map image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!heatFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  3D isometric warehouse heat map showing hot SKUs near pack
                  station
                </div>
              }
            >
              <img
                src="/blog-velocity-heat.jpg"
                alt="3D isometric view of a warehouse floor map on a tablet. Fast-moving Hot SKUs near the packing station glow red/orange; slow-moving items are cool blue. An arrow points from Hot Zone to Pack Station."
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setHeatFailed(true)}
              />
            </Show>
          </div>

          {/* Section 3: Dynamic Velocity */}
          <h2 class={h2}>
            Dynamic Velocity: The Number That Changes Every Day
          </h2>
          <p class={p}>
            Velocity isn&apos;t a fixed property of a product — it&apos;s a live
            measurement. A product that sold 2 units per day last month might be
            running at 8 units per day this week after a social media mention
            from a local influencer. A product that always moved well in the
            summer might already be slowing as autumn approaches.
          </p>
          <p class={p}>
            When your system tracks velocity in real time, reorder points
            aren&apos;t calculated once a month — they update continuously. The
            system doesn&apos;t wait for you to notice the shelf is almost
            empty. It calculates:{' '}
            <em>
              &ldquo;At current velocity, this SKU will reach zero in 4 days.
              Supplier lead time is 5 days. A reorder is overdue.&rdquo;
            </em>
          </p>
          <p class={p}>
            That shift — from{' '}
            <strong class="font-semibold text-gray-900">
              reactive stockouts to proactive restocking
            </strong>{' '}
            — is the entire value proposition of AI-powered inventory.
          </p>

          {/* Technical tidbit: cross-product correlation */}
          <h3 class={h3}>The Hidden Correlations No Spreadsheet Can Surface</h3>
          <p class={p}>
            Here&apos;s the kind of insight that illustrates where inventory AI
            is heading — and why the gap between spreadsheet management and
            data-driven systems keeps widening:
          </p>
          <div class="mt-6 rounded-2xl border border-gray-200 bg-gray-50 p-6">
            <p class="text-sm font-bold uppercase tracking-wide text-gray-500">
              The type of pattern advanced AI surfaces
            </p>
            <p class="mt-3 text-base leading-8 text-gray-700">
              &ldquo;When Product A (a premium cleaning solvent) sells on
              Tuesday, Product B (a refill pack for the same line) reliably
              spikes on Thursday — with a 78% correlation across the last 6
              months.&rdquo;
            </p>
            <p class="mt-3 text-sm text-gray-500">
              A human reviewing a spreadsheet sees two separate SKUs. An AI
              system sees a paired buying pattern with a 48-hour lag — and can
              flag it as a restocking signal before the shelf empties. This is
              the frontier of what inventory intelligence is being built to do.
            </p>
          </div>
          <p class={p}>
            Your catalog likely contains dozens of patterns like this: seasonal
            bundles, complementary SKUs, products that react to the same
            promotions. The first step toward surfacing them is having accurate
            per-SKU velocity data in the first place — which is exactly where
            Khoai starts.
          </p>

          {/* Section 4: Supplier Reliability Score */}
          <h2 class={h2}>
            The Supplier Reliability Score: Knowing Which Lead Times to Actually
            Trust
          </h2>
          <p class={p}>
            Here&apos;s a decision every warehouse manager makes intuitively but
            rarely measures: your supplier says they deliver in 5 days. Do you
            believe them? Do you reorder 6 days before you need the stock — or
            12, just in case?
          </p>
          <p class={p}>
            Khoai calculates a{' '}
            <strong class="font-semibold text-gray-900">
              supplier reliability score (0–100)
            </strong>{' '}
            for each of your suppliers, derived from the consistency of their
            actual historical delivery times. A supplier who delivers in 4–6
            days every time scores near 100. One whose lead times swing between
            3 days and 18 days scores much lower.
          </p>
          <p class={p}>
            That score has a direct, concrete effect on your reorder point
            calculations:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                High reliability (80–100):
              </strong>{' '}
              reorder window is calculated against their stated lead time — you
              can cut it close.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Medium reliability (40–79):
              </strong>{' '}
              a safety buffer is applied automatically — the reorder alert fires
              earlier than you might expect.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Low reliability (below 40):
              </strong>{' '}
              urgency tiers activate sooner and suggested reorder quantities
              increase to cover potential delivery gaps.
            </li>
          </ul>
          <p class={p}>
            The result: you stop making gut-feel judgments about which supplier
            to trust this week. The data tells you — and the reorder logic
            adjusts automatically around it.
          </p>

          {/* Dashboard image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!dashFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Flat UI infographic showing AI prediction engine with outputs
                </div>
              }
            >
              <img
                src="/blog-ai-dashboard.jpg"
                alt="Flat-design UI infographic. A central Prediction Engine brain icon receives data lines from Sales History, Lead Times, and Local Events, then outputs two boxes: Suggested PO: 50 Units and Safety Stock Alert."
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setDashFailed(true)}
              />
            </Show>
          </div>

          {/* Section 5: Automated POs */}
          <h2 class={h2}>
            From Forecast to Purchase Order: Closing the Loop in One Click
          </h2>
          <p class={p}>
            Knowing you need to reorder is only half the job. The other half is
            actually doing it — which, in most manual operations, means opening
            a supplier contact, writing up a request, cross-checking which items
            are running low, and confirming delivery windows. For a warehouse
            with 200 active SKUs, that process can consume hours every week.
          </p>
          <p class={p}>
            When AI analytics are connected directly to your supplier catalog,
            the forecast becomes a pre-filled purchase order: SKU, quantity,
            preferred supplier, expected lead time. The manager reviews it,
            makes any adjustments, and approves with a single confirmation. The
            preparation work is done automatically.
          </p>
          <p class={p}>
            The result isn&apos;t just efficiency — it&apos;s{' '}
            <strong class="font-semibold text-gray-900">consistency</strong>.
            Manual purchasing is subject to whoever happened to do the reorder
            that week and what they remembered to check. Automated, AI-driven
            purchasing is consistent every time, for every SKU, regardless of
            who is on shift.
          </p>

          {/* Section 6: Start Small */}
          <h2 class={h2}>You Don&apos;t Need a Large Catalog to Start</h2>
          <p class={p}>
            A common hesitation: &ldquo;we don&apos;t have enough data for AI to
            be useful yet.&rdquo; This is true for training a model from scratch
            — but not for a system that ships with pre-built forecasting models
            tuned to general retail patterns.
          </p>
          <p class={p}>
            The practical approach: start with your top 20 fastest-moving SKUs.
            Get velocity tracking and reorder alerts running for those. Within
            two to four weeks, the system has enough of your own real data to
            meaningfully improve its predictions for those specific products.
            Then expand to the next 50.
          </p>
          <p class={p}>
            AI forecasting isn&apos;t a flip-a-switch transformation. It&apos;s
            a compounding advantage — one that gets more accurate the longer it
            runs with your real operational data. The best time to start was six
            months ago. The second best time is today.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Stop reacting. Start predicting.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Khoai&apos;s AI analytics surface real-time sales velocity, flag
              reorder opportunities before stockouts happen, and generate
              supplier purchase orders automatically. Start free — no credit
              card required.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Try Khoai free →
            </A>
          </div>
        </div>

        {/* Back to blog */}
        <div class="mt-16 border-t border-gray-200 pt-8">
          <A
            href="/blogs"
            class="text-sm font-semibold text-amber-600 hover:text-amber-700"
          >
            ← Back to all posts
          </A>
        </div>
      </article>
    </PublicLayout>
  );
}
